import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Card,
    CardContent,
    InputAdornment,
    Paper,
    Divider,
    Avatar,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Snackbar,
    Alert,
    CircularProgress,
    Checkbox
} from '@mui/material';
import {
    Close as CloseIcon,
    Event as EventIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    AttachMoney as PriceIcon,
    Inventory as InventoryIcon,
    Category as CategoryIcon,
    Notes as NotesIcon,
    Schedule as ScheduleIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    List as ListIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const EventItemEnquiryModalSingle = ({
    open,
    onClose,
    onSave,
    enquiryData = null,
    mode = 'create',
    categories = [],
    customers = [],
    items = []
}) => {
    const { companyId, branchId, token, userId, permissions } = useAuth();

    const hasCreateMasterPermission = permissions?.includes('Create Master');
    const hasEditMasterPermission = permissions?.includes('Edit Master');
    const hasDeleteMasterPermission = permissions?.includes('Delete Master');

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        EnquiryNo: '',
        EnquiryNumber: '',
        BillNumber: '',
        BillPrefix: '',
        CustomerId: '',
        CustomerName: '',
        CustomerTypeId: 1, // 1 = Events, 2 = Others
        ContactNumber1: '',
        ContactNumber2: '',
        GSTNumber: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        BillDate: '',
        Status: 'Pending', // Default status
        EventName: '',
        EventLocation: '',
        EventAddress: '',
        EventDays: '',
        ExpectedTime: '',
        ProgramDate: '',
        DeliveryDate: '',
        DeliveryReturnDate: '',
        TransportingCharge: '0',
        Amount: '',
        DiscountAmount: '',
        RoundOff: '0',
        NetAmount: '',
        TotalAmount: '',
        Remarks: '',
        FinYear: new Date().getFullYear().toString(),
        // Items will be handled separately
        items: [
            {
                ItemId: '',
                ItemName: '',
                Quantity: '1',
                Rate: '',
                Amount: '',
                DiscountPercentage: '0',
                DiscountAmount: '',
                HSNCode: '',
                GSTRate: '18',
                TaxableAmount: '',
                CGSTAmount: '',
                SGSTAmount: '',
                IGSTAmount: '',
                TotalAmount: ''
            }
        ]
    });

    const [errors, setErrors] = useState({});
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [isBlacklisted, setIsBlacklisted] = useState(false);

    // Get Items modal state
    const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
    const [getItemsLoading, setGetItemsLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
    const [currentItemsPage, setCurrentItemsPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [addingItemsLoading, setAddingItemsLoading] = useState(false);
    
    // Forecast validation state
    const [forecastData, setForecastData] = useState([]);
    const [forecastLoading, setForecastLoading] = useState(false);
    const [itemConflicts, setItemConflicts] = useState({}); // Store conflicts by ItemId
    
    // Helper function to convert 24-hour time to 12-hour AM/PM format
    const formatTimeToAMPM = (time24) => {
        if (!time24) return 'N/A';
        
        const [hours, minutes] = time24.split(':');
        let hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        
        hour = hour % 12;
        hour = hour ? hour : 12; // 0 should be 12
        
        return `${hour}:${minutes} ${ampm}`;
    };
    
    // Helper function to render field based on mode
    const renderField = (label, value, fieldProps = {}) => {
        return (
            <TextField
                fullWidth
                label={label}
                value={value}
                size="small"
                InputProps={{ readOnly: mode === 'view' }}
                sx={{
                    '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                }}
                {...fieldProps}
            />
        );
    };
    
    // Fetch bill number for enquiry
    const fetchBillNumberForEnquiry = async () => {
        if (mode !== 'create') return;

        try {
            const response = await fetchData('EnquiryApi', `/MaxBillno/${companyId}/${branchId}`, token);
            const data = response.response_data || response || {};
            const billNumber = data.BillNumber || data.billNumber || 1;
            const billPrefix = data.BillPrefix || data.billPrefix || '';
            const finYear = data.FinYear || data.finYear || '';

            // Generate EnquiryNo from BillPrefix + BillNumber
            const enquiryNo = `${billPrefix}/${String(billNumber)}/${finYear}`;

            setFormData(prev => ({
                ...prev,
                BillNumber: billNumber,
                BillPrefix: billPrefix,
                EnquiryNo: enquiryNo,
                FinYear: finYear
            }));
        } catch (error) {
            console.error('Error fetching bill number for enquiry:', error);
        }
    };

    useEffect(() => {
        if (open) {
            if (enquiryData && mode !== 'create') {
                console.log('Loading enquiry data in modal:', enquiryData);
                console.log('EnquiryDetails:', enquiryData.EnquiryDetails);
                console.log('Items:', enquiryData.items);
                
                setFormData({
                    Id: enquiryData.Id || '',
                    EnquiryNo: enquiryData.EnquiryNo || '',
                    EnquiryNumber: enquiryData.EnquiryNumber || '',
                    CustomerId: enquiryData.CustomerId || '',
                    CustomerName: enquiryData.CustomerName || '',
                    CustomerTypeId: enquiryData.CustomerTypeId || 1,
                    ContactNumber1: enquiryData.ContactNumber1 || '',
                    ContactNumber2: enquiryData.ContactNumber2 || '',
                    GSTNumber: enquiryData.GSTNumber || '',
                    AddressLine1: enquiryData.AddressLine1 || '',
                    AddressLine2: enquiryData.AddressLine2 || '',
                    Location: enquiryData.Location || '',
                    PinCode: enquiryData.PinCode || '',
                    BillDate: enquiryData.BillDate ? enquiryData.BillDate.split('T')[0] : '',
                    Status: enquiryData.Status || 'Pending',
                    EventName: enquiryData.EventName || '',
                    EventLocation: enquiryData.EventLocation || '',
                    EventAddress: enquiryData.EventAddress || '',
                    EventDays: enquiryData.EventDays || '',
                    ExpectedTime: enquiryData.ExpectedTime || '',
                    ProgramDate: enquiryData.ProgramDate ? enquiryData.ProgramDate.split('T')[0] : '',
                    DeliveryDate: enquiryData.DeliveryDate ? enquiryData.DeliveryDate.split('T')[0] : '',
                    DeliveryReturnDate: enquiryData.DeliveryReturnDate ? enquiryData.DeliveryReturnDate.split('T')[0] : '',
                    TransportingCharge: enquiryData.TransportingCharge || '0',
                    Amount: enquiryData.Amount || '',
                    DiscountAmount: enquiryData.DiscountAmount || '',
                    RoundOff: enquiryData.RoundOff || '0',
                    NetAmount: enquiryData.NetAmount || '',
                    TotalAmount: enquiryData.TotalAmount || '',
                    Remarks: enquiryData.Remarks || '',
                    FinYear: enquiryData.FinYear || new Date().getFullYear().toString(),
                    items: (enquiryData.EnquiryDetails || enquiryData.items || []).map(item => ({
                        ItemId: item.ItemId || '',
                        ItemName: item.ItemName || '',
                        Quantity: item.Quantity?.toString() || '1',
                        Rate: item.Rate?.toString() || '',
                        Amount: item.Amount?.toString() || '',
                        DiscountPercentage: item.DiscountPercentage?.toString() || '0',
                        DiscountAmount: item.DiscountAmount?.toString() || '',
                        HSNCode: item.HSNCode || '',
                        GSTRate: item.GSTRate?.toString() || '18',
                        TaxableAmount: item.TaxableAmount?.toString() || '',
                        CGSTAmount: item.CGSTAmount?.toString() || '',
                        SGSTAmount: item.SGSTAmount?.toString() || '',
                        IGSTAmount: item.IGSTAmount?.toString() || '',
                        TotalAmount: item.TotalAmount?.toString() || ''
                    }))
                });
            } else {
                const today = new Date().toISOString().split('T')[0];

                setFormData({
                    EnquiryNo: '',
                    EnquiryNumber: '',
                    CustomerId: '',
                    CustomerName: '',
                    CustomerTypeId: 1,
                    ContactNumber1: '',
                    ContactNumber2: '',
                    GSTNumber: '',
                    AddressLine1: '',
                    AddressLine2: '',
                    Location: '',
                    PinCode: '',
                    BillDate: today,
                    Status: 'Pending',
                    EventName: '',
                    EventLocation: '',
                    EventAddress: '',
                    EventDays: '',
                    ExpectedTime: '',
                    ProgramDate: '',
                    DeliveryDate: '',
                    DeliveryReturnDate: '',
                    TransportingCharge: '0',
                    Amount: '',
                    DiscountAmount: '',
                    RoundOff: '0',
                    NetAmount: '',
                    TotalAmount: '',
                    Remarks: '',
                    FinYear: new Date().getFullYear().toString(),
                    items: [
                        {
                            ItemId: '',
                            ItemName: '',
                            Quantity: '1',
                            Rate: '',
                            Amount: '',
                            DiscountPercentage: '0',
                            DiscountAmount: '',
                            HSNCode: '',
                            GSTRate: '18',
                            TaxableAmount: '',
                            CGSTAmount: '',
                            SGSTAmount: '',
                            IGSTAmount: '',
                            TotalAmount: ''
                        }
                    ]
                });
            }
            setErrors({});
            setShowNewCustomerForm(false);
            setShowSuccessMessage(false);
            setSuccessMessage('');
            setShowSuccessDialog(false);
            setIsBlacklisted(false);

            // Fetch bill number for new enquiry
            if (mode === 'create') {
                fetchBillNumberForEnquiry();
            }
        }
    }, [open, enquiryData, mode]);

    // Auto-calculate ReturnDate when DeliveryDate or EventDays changes
    useEffect(() => {
        if (formData.DeliveryDate && formData.EventDays) {
            const deliveryDate = new Date(formData.DeliveryDate);
            const eventDays = parseFloat(formData.EventDays) || 0;
            
            // ReturnDate = DeliveryDate + EventDays + 1
            const returnDate = new Date(deliveryDate);
            returnDate.setDate(returnDate.getDate() + eventDays + 1);
            
            const returnDateStr = returnDate.toISOString().split('T')[0];
            
            // Only update if different to prevent infinite loop
            if (returnDateStr !== formData.DeliveryReturnDate) {
                setFormData(prev => ({
                    ...prev,
                    DeliveryReturnDate: returnDateStr
                }));
            }
        }
    }, [formData.DeliveryDate, formData.EventDays]);
    
    const handleInputChange = (field, value, itemIndex = null) => {
        if (itemIndex !== null) {
            // Handle item-specific changes
            setFormData(prev => {
                const newItems = [...prev.items];
                newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    [field]: value
                };
                return {
                    ...prev,
                    items: newItems
                };
            });

            // Auto-populate item details and calculate amounts
            if (field === 'ItemId') {
                // Check for duplicate item
                const isDuplicate = formData.items.some((item, index) =>
                    index !== itemIndex && item.ItemId === value && value !== ''
                );

                if (isDuplicate) {
                    // Show error and don't allow selection
                    setErrors(prev => ({
                        ...prev,
                        [`ItemId_${itemIndex}`]: 'This item is already selected. Duplicate items are not allowed.'
                    }));
                    return;
                } else {
                    // Clear any existing error for this field
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`ItemId_${itemIndex}`];
                        return newErrors;
                    });
                }

                const selectedItem = items.find(item => item.Id === value);
                if (selectedItem) {
                    const customerTypeId = formData.CustomerTypeId;
                    const rate = customerTypeId === 1 ? selectedItem.EventSaleRate : selectedItem.OthersSaleRate;

                    setFormData(prev => {
                        const newItems = [...prev.items];
                        newItems[itemIndex] = {
                            ...newItems[itemIndex],
                            ItemName: selectedItem.Name || '',
                            HSNCode: selectedItem.HSNCode || '',
                            Rate: rate || selectedItem.Rate || '',
                            GSTRate: selectedItem.GSTRate || '18',
                            Quantity: '1' // Set default quantity to 1
                        };
                        return {
                            ...prev,
                            items: newItems
                        };
                    });

                    // Calculate amounts with default quantity of 1
                    setTimeout(() => {
                        calculateItemAmounts(itemIndex, 'Quantity', '1');
                    }, 0);
                }
            }

            // Calculate amounts only for quantity, rate, and discount changes
            if (field === 'Quantity' || field === 'Rate' || field === 'DiscountPercentage') {
                calculateItemAmounts(itemIndex, field, value);
            }
        } else {
            // Handle main form changes
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            if (errors[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: ''
                }));
            }

            // Auto-populate customer details
            if (field === 'CustomerId') {
                const selectedCustomer = customers.find(customer => customer.Id === value);
                if (selectedCustomer) {
                    // Check if customer is blacklisted
                    setIsBlacklisted(selectedCustomer.Blacklist === true || selectedCustomer.Blacklist === 1);

                    setFormData(prev => ({
                        ...prev,
                        CustomerName: selectedCustomer.Name || '',
                        CustomerTypeId: selectedCustomer.CustomerTypeId || 1,
                        ContactNumber1: selectedCustomer.ContactNumber1 || selectedCustomer.ContactNumber || '',
                        ContactNumber2: selectedCustomer.ContactNumber2 || '',
                        GSTNumber: selectedCustomer.GSTNumber || '',
                        AddressLine1: selectedCustomer.AddressLine1 || selectedCustomer.Address || '',
                        AddressLine2: selectedCustomer.AddressLine2 || '',
                        Location: selectedCustomer.Location || '',
                        PinCode: selectedCustomer.PinCode || ''
                    }));

                    // Update rates for all items based on customer type
                    updateItemRatesForCustomerType(selectedCustomer.CustomerTypeId || 1);
                } else {
                    setIsBlacklisted(false);
                }
            }

            // Update item rates when customer type changes (for manual entry)
            if (field === 'CustomerTypeId') {
                updateItemRatesForCustomerType(value);
            }

            // Auto-calculate Return Date when Event Days or Delivery Date changes
            if (field === 'EventDays' || field === 'DeliveryDate' || field === 'ProgramDate') {
                calculateReturnDate(field, value);
            }

            // Recalculate totals when transport charge or round off changes
            if (field === 'TransportingCharge' || field === 'RoundOff') {
                recalculateTotals();
            }
        }
    };
    const calculateReturnDate = (changedField, changedValue) => {
        setFormData(prev => {
            const eventDays = changedField === 'EventDays' ? parseFloat(changedValue) || 0 : parseFloat(prev.EventDays) || 0;
            const deliveryDate = changedField === 'DeliveryDate' ? changedValue : prev.DeliveryDate;
            const programDate = changedField === 'ProgramDate' ? changedValue : prev.ProgramDate;

            // Auto-calculate delivery date (1 day before program date)
            let calculatedDeliveryDate = deliveryDate;
            if (changedField === 'ProgramDate' && programDate) {
                try {
                    const program = new Date(programDate);
                    const delivery = new Date(program);
                    delivery.setDate(program.getDate() - 1);
                    calculatedDeliveryDate = delivery.toISOString().split('T')[0];
                } catch (error) {
                    console.error('Error calculating delivery date:', error);
                }
            }

            // Calculate return date: Delivery Date + Event Days + 1
            let calculatedReturnDate = prev.DeliveryReturnDate;
            if (eventDays > 0 && calculatedDeliveryDate) {
                try {
                    const delivery = new Date(calculatedDeliveryDate);
                    const returnDate = new Date(delivery);
                    returnDate.setDate(delivery.getDate() + eventDays + 1); // +1 additional day
                    calculatedReturnDate = returnDate.toISOString().split('T')[0];
                } catch (error) {
                    console.error('Error calculating return date:', error);
                }
            }

            return {
                ...prev,
                DeliveryDate: calculatedDeliveryDate,
                DeliveryReturnDate: calculatedReturnDate
            };
        });
    };

    const updateItemRatesForCustomerType = (customerTypeId) => {
        setFormData(prev => {
            const newItems = prev.items.map(item => {
                if (item.ItemId) {
                    const selectedItem = items.find(i => i.Id === item.ItemId);
                    if (selectedItem) {
                        const rate = customerTypeId === 1 ? selectedItem.EventSaleRate : selectedItem.OthersSaleRate;
                        return {
                            ...item,
                            Rate: rate || selectedItem.Rate || ''
                        };
                    }
                }
                return item;
            });
            return {
                ...prev,
                items: newItems
            };
        });
    };

    const calculateItemAmounts = (itemIndex, changedField, changedValue) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            const item = newItems[itemIndex];

            const quantity = changedField === 'Quantity' ? parseFloat(changedValue) || 0 : parseFloat(item.Quantity) || 0;
            const rate = changedField === 'Rate' ? parseFloat(changedValue) || 0 : parseFloat(item.Rate) || 0;
            const discountPercentage = changedField === 'DiscountPercentage' ? parseFloat(changedValue) || 0 : parseFloat(item.DiscountPercentage) || 0;
            const gstRate = parseFloat(item.GSTRate) || 0;

            const amount = quantity * rate;
            const discountAmount = (amount * discountPercentage) / 100;
            const taxableAmount = amount - discountAmount;

            const cgstAmount = (taxableAmount * gstRate) / 200; // CGST is half of total GST
            const sgstAmount = (taxableAmount * gstRate) / 200; // SGST is half of total GST
            const igstAmount = 0; // For now, assuming intra-state

            const totalAmount = taxableAmount + cgstAmount + sgstAmount + igstAmount;

            newItems[itemIndex] = {
                ...item,
                Amount: amount.toFixed(2),
                DiscountAmount: discountAmount.toFixed(2),
                TaxableAmount: taxableAmount.toFixed(2),
                CGSTAmount: cgstAmount.toFixed(2),
                SGSTAmount: sgstAmount.toFixed(2),
                IGSTAmount: igstAmount.toFixed(2),
                TotalAmount: totalAmount.toFixed(2)
            };

            // Calculate overall totals
            const overallAmount = newItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
            const overallDiscountAmount = newItems.reduce((sum, item) => sum + parseFloat(item.DiscountAmount || 0), 0);
            const overallTotalAmount = newItems.reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0);
            const transportCharge = parseFloat(prev.TransportingCharge) || 0;
            const roundOff = parseFloat(prev.RoundOff) || 0;
            const netAmount = overallTotalAmount + transportCharge + roundOff;

            return {
                ...prev,
                items: newItems,
                Amount: overallAmount.toFixed(2),
                DiscountAmount: overallDiscountAmount.toFixed(2),
                TotalAmount: overallTotalAmount.toFixed(2),
                NetAmount: netAmount.toFixed(2)
            };
        });
        
        // Re-check forecast conflicts when quantity changes
        if (changedField === 'Quantity' && forecastData.length > 0) {
            // Use setTimeout to ensure state has updated
            setTimeout(() => {
                checkForecastConflicts(forecastData);
            }, 100);
        }
    };

    const recalculateTotals = () => {
        setFormData(prev => {
            const overallAmount = prev.items.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
            const overallDiscountAmount = prev.items.reduce((sum, item) => sum + parseFloat(item.DiscountAmount || 0), 0);
            const overallTotalAmount = prev.items.reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0);
            const transportCharge = parseFloat(prev.TransportingCharge) || 0;
            const roundOff = parseFloat(prev.RoundOff) || 0;
            const netAmount = overallTotalAmount + transportCharge + roundOff;

            return {
                ...prev,
                Amount: overallAmount.toFixed(2),
                DiscountAmount: overallDiscountAmount.toFixed(2),
                TotalAmount: overallTotalAmount.toFixed(2),
                NetAmount: netAmount.toFixed(2)
            };
        });
    };
    const addItem = () => {
        // Instead of adding a blank item, open the Get Items modal
        handleOpenGetItemsModal();
    };

    const removeItem = (itemIndex) => {
        if (formData.items.length > 1) {
            // Multiple items - remove the selected item
            setFormData(prev => {
                const newItems = prev.items.filter((_, index) => index !== itemIndex);

                // Recalculate totals
                const overallAmount = newItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
                const overallDiscountAmount = newItems.reduce((sum, item) => sum + parseFloat(item.DiscountAmount || 0), 0);
                const overallTotalAmount = newItems.reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0);
                const transportCharge = parseFloat(prev.TransportingCharge) || 0;
                const roundOff = parseFloat(prev.RoundOff) || 0;
                const netAmount = overallTotalAmount + transportCharge + roundOff;

                return {
                    ...prev,
                    items: newItems,
                    Amount: overallAmount.toFixed(2),
                    DiscountAmount: overallDiscountAmount.toFixed(2),
                    TotalAmount: overallTotalAmount.toFixed(2),
                    NetAmount: netAmount.toFixed(2)
                };
            });
        } else {
            // Single item - reset to empty values
            setFormData(prev => {
                const newItems = [{
                    ItemId: '',
                    ItemName: '',
                    Quantity: '1',
                    Rate: '',
                    Amount: '',
                    DiscountPercentage: '0',
                    DiscountAmount: '',
                    HSNCode: '',
                    GSTRate: '18',
                    TaxableAmount: '',
                    CGSTAmount: '',
                    SGSTAmount: '',
                    IGSTAmount: '',
                    TotalAmount: ''
                }];

                return {
                    ...prev,
                    items: newItems,
                    Amount: '0',
                    DiscountAmount: '0',
                    TotalAmount: '0',
                    NetAmount: '0'
                };
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Customer validation
        if (!formData.CustomerId && !showNewCustomerForm) newErrors.CustomerId = 'Customer is required';
        if (showNewCustomerForm && !formData.CustomerName) newErrors.CustomerName = 'Customer name is required';
        if (!formData.BillDate) newErrors.BillDate = 'Bill date is required';

        // Item validation - only validate items that have been selected (have ItemId)
        const selectedItems = formData.items.filter(item => item.ItemId);
        
        if (selectedItems.length === 0) {
            newErrors.items = 'At least one item is required';
        }
        
        selectedItems.forEach((item, index) => {
            const originalIndex = formData.items.findIndex(originalItem => 
                originalItem.ItemId === item.ItemId && 
                originalItem.ItemName === item.ItemName
            );
            
            if (!item.Quantity || item.Quantity <= 0) {
                newErrors[`Quantity_${originalIndex}`] = `Valid quantity is required`;
            }
            if (!item.Rate || item.Rate <= 0) {
                newErrors[`Rate_${originalIndex}`] = `Valid rate is required`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            // Prepare Enquiry main object (without items)
            const enquiryData = {
                EnquiryNo: formData.EnquiryNo,
                EnquiryNumber: formData.BillNumber,
                BillNumber: formData.BillNumber,
                BillPrefix: formData.BillPrefix,
                CustomerId: formData.CustomerId,
                CustomerName: formData.CustomerName,
                CustomerTypeId: formData.CustomerTypeId,
                ContactNumber1: formData.ContactNumber1,
                ContactNumber2: formData.ContactNumber2,
                GSTNumber: formData.GSTNumber,
                AddressLine1: formData.AddressLine1,
                AddressLine2: formData.AddressLine2,
                Location: formData.Location,
                PinCode: formData.PinCode,
                BillDate: formData.BillDate,
                Status: formData.Status,
                EventName: formData.EventName,
                EventLocation: formData.EventLocation,
                EventAddress: formData.EventAddress,
                EventDays: parseFloat(formData.EventDays) || 0,
                ExpectedTime: formData.ExpectedTime,
                ProgramDate: formData.ProgramDate,
                DeliveryDate: formData.DeliveryDate,
                DeliveryReturnDate: formData.DeliveryReturnDate,
                TransportingCharge: parseFloat(formData.TransportingCharge) || 0,
                Amount: parseFloat(formData.Amount) || 0,
                DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
                RoundOff: parseFloat(formData.RoundOff) || 0,
                NetAmount: parseFloat(formData.NetAmount) || 0,
                TotalAmount: parseFloat(formData.TotalAmount) || 0,
                Remarks: formData.Remarks,
                FinYear: formData.FinYear,
                CompanyId: companyId,
                BranchId: branchId,
                CreatedBy: userId,
                ModifiedBy: userId,
                IsActive: true
            };

            // Prepare EnquiryDetails array (items) - only include items with ItemId
            const enquiryDetails = formData.items
                .filter(item => item.ItemId) // Only include selected items
                .map(item => ({
                    ItemId: item.ItemId,
                    ItemName: item.ItemName,
                    Quantity: parseFloat(item.Quantity) || 0,
                    Rate: parseFloat(item.Rate) || 0,
                    Amount: parseFloat(item.Amount) || 0,
                    DiscountPercentage: parseFloat(item.DiscountPercentage) || 0,
                    DiscountAmount: parseFloat(item.DiscountAmount) || 0,
                    HSNCode: item.HSNCode,
                GSTRate: parseFloat(item.GSTRate) || 0,
                TaxableAmount: parseFloat(item.TaxableAmount) || 0,
                CGSTAmount: parseFloat(item.CGSTAmount) || 0,
                SGSTAmount: parseFloat(item.SGSTAmount) || 0,
                IGSTAmount: parseFloat(item.IGSTAmount) || 0,
                TotalAmount: parseFloat(item.TotalAmount) || 0,
                CompanyId: companyId,
                BranchId: branchId,
                IsActive: true
            }));

            // Combine data structure as expected by API
            const enquiryVM = {
                Enquiry: enquiryData,
                EnquiryDetails: enquiryDetails
            };

            const dataToSend = {
                Enquiry: enquiryData,
                EnquiryDetails: enquiryDetails
            };

            let response;
            if (mode === 'edit' && formData.Id) {
                // Add Id to enquiry data for edit mode
                dataToSend.Enquiry.Id = formData.Id;
                response = await updateData('EnquiryApi', formData.Id, dataToSend, token);
            } else {
                response = await postData('EnquiryApi', enquiryVM, token);
            }

            console.log('Enquiry API Response:', response);

            // Check for success - handle multiple response formats
            const isSuccess = response && (
                response.responseCode === '1111' || 
                response.responseCode === 1111 || 
                response.success === true ||
                response.status === 200 || 
                response.status === 201 ||
                (response.data && response.data.success) ||
                !response.error
            );

            if (isSuccess) {
                // Refresh data immediately before closing
                onSave();
                
                // Close the main modal immediately
                onClose();
                
                // Show success dialog
                const message = mode === 'edit' ? 'Enquiry updated successfully!' : 'Enquiry created successfully!';
                setSuccessMessage(message);
                setShowSuccessDialog(true);

                // Auto-close success dialog after 3 seconds
                setTimeout(() => {
                    setShowSuccessDialog(false);
                }, 3000);
            } else {
                // Show error message if response is not successful
                setSuccessMessage('Failed to save enquiry. Please try again.');
                setShowSuccessMessage(true);
            }
        } catch (error) {
            console.error('Error saving enquiry:', error);
            setSuccessMessage('Error occurred while saving enquiry. Please try again.');
            setShowSuccessMessage(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewCustomer = () => {
        setShowNewCustomerForm(true);
        setIsBlacklisted(false);
        setFormData(prev => ({
            ...prev,
            CustomerId: '',
            CustomerName: '',
            ContactNumber1: '',
            ContactNumber2: '',
            GSTNumber: '',
            AddressLine1: '',
            AddressLine2: '',
            Location: '',
            PinCode: ''
        }));
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
    };

    // Handle Get Items modal
    const handleOpenGetItemsModal = async () => {
        setGetItemsModalOpen(true);
        setGetItemsLoading(true);
        setSelectedItems([]);
        setGetItemsSearchValue('');
        setCurrentItemsPage(1);

        const loadingTimeout = setTimeout(() => {
            console.warn('Get Items loading timeout - stopping loading');
            setGetItemsLoading(false);
        }, 3000);

        try {
            // Items are already loaded from props, so just stop loading
            setGetItemsLoading(false);
        } finally {
            clearTimeout(loadingTimeout);
            setGetItemsLoading(false);
        }
    };

    const handleCloseGetItemsModal = () => {
        setGetItemsModalOpen(false);
        setSelectedItems([]);
        setGetItemsSearchValue('');
    };

    // Handle item selection in Get Items modal
    const handleItemSelection = (item, isSelected) => {
        if (isSelected) {
            const customerTypeId = formData.CustomerTypeId;
            const rate = customerTypeId === 1 ? item.EventSaleRate : item.OthersSaleRate;

            setSelectedItems(prev => [...prev, {
                ...item,
                selectedQuantity: 1,
                selectedRate: rate || item.Rate || 0,
                selectedDiscountPercentage: 0
            }]);
        } else {
            setSelectedItems(prev => prev.filter(si => si.Id !== item.Id));
        }
    };

    // Handle search in Get Items modal
    const handleGetItemsSearch = (searchValue) => {
        setGetItemsSearchValue(searchValue);
        setCurrentItemsPage(1);
    };

    // Filter items based on search value for Get Items modal
    const getFilteredItems = () => {
        if (!getItemsSearchValue.trim()) {
            return items;
        }

        const searchTerm = getItemsSearchValue.toLowerCase().trim();
        return items.filter(item =>
            item.Name?.toLowerCase().includes(searchTerm) ||
            item.Code?.toLowerCase().includes(searchTerm) ||
            item.HSNCode?.toLowerCase().includes(searchTerm)
        );
    };

    // Get paginated items for Get Items modal
    const getPaginatedItems = () => {
        const filtered = getFilteredItems();
        const startIndex = (currentItemsPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    };

    const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

    // Handle selected item changes in Get Items modal
    const handleSelectedItemChange = (itemId, field, value) => {
        setSelectedItems(prev => prev.map(item =>
            item.Id === itemId ? { ...item, [field]: value } : item
        ));
    };

    // Check if item is already in sale
    const isItemAlreadyInSale = (itemId) => {
        return formData.items.some(item => item.ItemId === itemId);
    };

    // Handle adding selected items
    const handleAddSelectedItems = async () => {
        if (selectedItems.length === 0) {
            handleCloseGetItemsModal();
            return;
        }

        setAddingItemsLoading(true);

        try {
            const itemsToAdd = selectedItems.filter(item => !isItemAlreadyInSale(item.Id));

            if (itemsToAdd.length === 0) {
                handleCloseGetItemsModal();
                return;
            }

            // Convert selected items to enquiry detail format
            const newItems = itemsToAdd.map(item => {
                const quantity = parseFloat(item.selectedQuantity) || 1;
                const rate = parseFloat(item.selectedRate) || 0;
                const discountPercentage = parseFloat(item.selectedDiscountPercentage) || 0;
                const gstRate = parseFloat(item.GSTRate) || 18;

                const amount = quantity * rate;
                const discountAmount = (amount * discountPercentage) / 100;
                const taxableAmount = amount - discountAmount;
                const cgstAmount = (taxableAmount * gstRate) / 200;
                const sgstAmount = (taxableAmount * gstRate) / 200;
                const totalAmount = taxableAmount + cgstAmount + sgstAmount;

                return {
                    ItemId: item.Id,
                    ItemName: item.Name,
                    Quantity: quantity.toString(),
                    Rate: rate.toString(),
                    Amount: amount.toFixed(2),
                    DiscountPercentage: discountPercentage.toString(),
                    DiscountAmount: discountAmount.toFixed(2),
                    HSNCode: item.HSNCode || '',
                    GSTRate: gstRate.toString(),
                    TaxableAmount: taxableAmount.toFixed(2),
                    CGSTAmount: cgstAmount.toFixed(2),
                    SGSTAmount: sgstAmount.toFixed(2),
                    IGSTAmount: '0',
                    TotalAmount: totalAmount.toFixed(2)
                };
            });

            // Calculate the updated items list before setting state
            const updatedItemsList = [...formData.items, ...newItems];

            // Add new items to existing items
            setFormData(prev => {
                // Recalculate totals
                const overallAmount = updatedItemsList.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
                const overallDiscountAmount = updatedItemsList.reduce((sum, item) => sum + parseFloat(item.DiscountAmount || 0), 0);
                const overallTotalAmount = updatedItemsList.reduce((sum, item) => sum + parseFloat(item.TotalAmount || 0), 0);
                const transportCharge = parseFloat(prev.TransportingCharge) || 0;
                const roundOff = parseFloat(prev.RoundOff) || 0;
                const netAmount = overallTotalAmount + transportCharge + roundOff;

                return {
                    ...prev,
                    items: updatedItemsList,
                    Amount: overallAmount.toFixed(2),
                    DiscountAmount: overallDiscountAmount.toFixed(2),
                    TotalAmount: overallTotalAmount.toFixed(2),
                    NetAmount: netAmount.toFixed(2)
                };
            });

            handleCloseGetItemsModal();
            
            // Fetch forecast data after items are added with the updated items list
            await fetchForecastData(itemsToAdd.map(item => item.Id), updatedItemsList);
        } catch (error) {
            console.error('Error adding selected items:', error);
        } finally {
            setAddingItemsLoading(false);
        }
    };

    // Fetch forecast data for selected items
    const fetchForecastData = async (itemIds, itemsList = null) => {
        // Use provided itemsList or current formData.items
        const currentItems = itemsList || formData.items;
        
        if (!formData.ProgramDate || itemIds.length === 0) {
            return;
        }

        setForecastLoading(true);
        try {
            // Create comma-separated item IDs
            const itemIdsParam = itemIds.join(',');
            
            // Call forecast API
            const response = await fetchData(
                'EnquiryApi',
                `/ForecastDatewise/${formData.ProgramDate}/${companyId}/${itemIdsParam}`,
                token
            );

            const forecastList = response.response_data || response || [];
            setForecastData(forecastList);

            // Check for conflicts with the updated items list
            checkForecastConflicts(forecastList, currentItems);
        } catch (error) {
            console.error('Error fetching forecast data:', error);
            setForecastData([]);
        } finally {
            setForecastLoading(false);
        }
    };

    // Check for date conflicts between delivery dates and forecast data
    const checkForecastConflicts = (forecastList, itemsList = null) => {
        // Use provided itemsList or current formData.items
        const currentItems = itemsList || formData.items;
        
        if (!formData.DeliveryDate || !formData.DeliveryReturnDate) {
            setItemConflicts({});
            return;
        }

        const deliveryDate = new Date(formData.DeliveryDate);
        const returnDate = new Date(formData.DeliveryReturnDate);
        const conflicts = {};

        // Check each item in the form
        currentItems.forEach(item => {
            if (!item.ItemId) return;

            // Get the requested quantity for this item
            const requestedQty = parseFloat(item.Quantity) || 0;

            // Find forecast records for this item
            const itemForecasts = forecastList.filter(f => f.ItemId === item.ItemId);

            // Get all conflicting forecasts (overlapping dates)
            const conflictingForecasts = itemForecasts.filter(forecast => {
                if (!forecast.DeliveryDate || !forecast.DeliveryReturnDate) return false;

                const forecastDelivery = new Date(forecast.DeliveryDate);
                const forecastReturn = new Date(forecast.DeliveryReturnDate);

                // Check for date overlap
                // Overlap occurs if: (StartA <= EndB) and (EndA >= StartB)
                return (deliveryDate <= forecastReturn) && (returnDate >= forecastDelivery);
            });

            if (conflictingForecasts.length > 0) {
                // Get stock information from the first forecast record (all should have same stock info for the item)
                const firstForecast = conflictingForecasts[0];
                const totalStock = parseFloat(firstForecast.TotalStock) || 0;
                const currentlyOnRentQty = parseFloat(firstForecast.CurrentlyOnRentQty) || 0;
                const availableStock = parseFloat(firstForecast.AvailableStock) || 0;
                const stockStatus = firstForecast.StockStatus;

                // Calculate total booked quantity during the overlapping period
                const totalBookedQty = conflictingForecasts.reduce((sum, forecast) => {
                    return sum + (parseFloat(forecast.ForecastQty) || 0);
                }, 0);

                // Check if there's a quantity conflict
                // Use AvailableStock from forecast data
                const hasQuantityConflict = requestedQty > availableStock;

                // Format date helper
                const formatDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    const date = new Date(dateStr);
                    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                };

                // Create detailed message with customer names, dates, and quantities
                const conflictDetails = conflictingForecasts.map(f => 
                    `${f.CustomerName} (${formatDate(f.DeliveryDate)} - ${formatDate(f.DeliveryReturnDate)}) - Qty: ${f.ForecastQty || 0}`
                ).join(', ');

                let message = `Already booked: ${conflictDetails}`;
                
                // Add quantity information
                const stockInfo = `Requested: ${requestedQty}, Available: ${availableStock}/${totalStock} (On Rent: ${currentlyOnRentQty})`;
                
                // Add quantity and stock status information
                if (hasQuantityConflict) {
                    // Show stock status only if it exists and is not empty
                    message += ` | ${stockInfo}${stockStatus && stockStatus.trim() ? ` - ${stockStatus}` : ''}`;
                } else {
                    message += ` | ${stockInfo} ✓`;
                }

                conflicts[item.ItemId] = {
                    hasConflict: hasQuantityConflict, // Only mark as conflict if quantity is insufficient
                    hasDateOverlap: true, // Always true if we reach here
                    forecasts: conflictingForecasts,
                    totalBookedQty,
                    availableStock,
                    requestedQty,
                    totalStock,
                    currentlyOnRentQty,
                    stockStatus,
                    message
                };
            }
        });

        setItemConflicts(conflicts);
    };

    // Fetch forecast when Program Date changes and items exist
    useEffect(() => {
        if (formData.ProgramDate && formData.items.length > 0) {
            const itemIds = formData.items
                .filter(item => item.ItemId)
                .map(item => item.ItemId);
            
            if (itemIds.length > 0) {
                fetchForecastData(itemIds);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.ProgramDate]);

    if (!open) return null;
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh',
                    width: '90vw',
                    maxWidth: '1400px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 0.75,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{
                            bgcolor: 'rgba(255,255,255,0.25)',
                            width: 28,
                            height: 28,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                            <EventIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0, letterSpacing: '-0.5px' }}>
                                {mode === 'view' ? 'View Enquiry' : mode === 'edit' ? 'Edit Enquiry' : 'Create New Enquiry'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                            width: 28,
                            height: 28
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content - Single Page Layout */}
            <DialogContent sx={{ p: 1, bgcolor: '#f8fafc' }}>
                {loading && <LinearProgress sx={{ mb: 0.75, borderRadius: 1 }} />}

                <Grid container spacing={1.5}>
                    {/* Blacklist Warning */}
                    {isBlacklisted && (
                        <Grid item xs={12}>
                            <Alert
                                severity="error"
                                sx={{
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    '& .MuiAlert-icon': {
                                        fontSize: 18
                                    },
                                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': {
                                            opacity: 1,
                                        },
                                        '50%': {
                                            opacity: 0.8,
                                        },
                                    }
                                }}
                            >
                                ⚠️ BLACKLISTED CUSTOMER - Proceed with caution!
                            </Alert>
                        </Grid>
                    )}

                    {/* Customer Information Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: isBlacklisted ? '2px solid #ef4444' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: isBlacklisted ? '#fef2f2' : 'white',
                            boxShadow: isBlacklisted ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: isBlacklisted ? '0 6px 16px rgba(239, 68, 68, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Box sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '6px',
                                    background: isBlacklisted
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: isBlacklisted
                                        ? '0 4px 12px rgba(239, 68, 68, 0.4)'
                                        : '0 4px 12px rgba(102, 126, 234, 0.3)'
                                }}>
                                    {isBlacklisted ? (
                                        <WarningIcon sx={{ color: 'white', fontSize: 12 }} />
                                    ) : (
                                        <PersonIcon sx={{ color: 'white', fontSize: 12 }} />
                                    )}
                                </Box>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 700, 
                                    color: isBlacklisted ? '#dc2626' : '#1a202c', 
                                    fontSize: '0.75rem', 
                                    letterSpacing: '-0.3px' 
                                }}>
                                    Customer Information {isBlacklisted && '⚠️'}
                                </Typography>
                            </Box>

                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={4}>
                                    {!showNewCustomerForm ? (
                                        <Box>
                                            <Autocomplete
                                                options={customers}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') return option;
                                                    const phone = option.ContactNumber1 || option.ContactNumber || '';
                                                    const type = option.CustomerTypeId === 1 ? 'Events' : 'Others';
                                                    return `${option.Name} - ${phone} (${type})`;
                                                }}
                                                value={customers.find(c => c.Id === formData.CustomerId) || null}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        handleInputChange('CustomerId', newValue.Id);
                                                    } else {
                                                        handleInputChange('CustomerId', '');
                                                    }
                                                }}
                                                disabled={mode === 'view'}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Customer by Name or Phone *"
                                                        error={!!errors.CustomerId}
                                                        helperText={errors.CustomerId}
                                                        size="small"
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                height: '28px',
                                                                fontSize: '0.75rem',
                                                                borderRadius: '6px'
                                                            },
                                                            '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 },
                                                            '& .MuiInputBase-input': { py: 0.5 },
                                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.25 },
                                                            '& .MuiOutlinedInput-root': {
                                                                '&:hover fieldset': {
                                                                    borderColor: '#667eea',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: '#667eea',
                                                                    borderWidth: '2px'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                                componentsProps={{
                                                    paper: {
                                                        sx: {
                                                            fontSize: '0.75rem',
                                                            '& .MuiAutocomplete-option': {
                                                                fontSize: '0.75rem',
                                                                minHeight: '32px',
                                                                py: 0.5
                                                            }
                                                        }
                                                    }
                                                }}
                                                size="small"
                                            />
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={handleCreateNewCustomer}
                                                sx={{ mt: 0.25, textTransform: 'none', fontSize: '0.65rem', py: 0.25, minHeight: 'auto' }}
                                            >
                                                + Create New Customer
                                            </Button>
                                        </Box>
                                    ) : (
                                        mode === 'view' ? (
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                    Customer Name *
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                    {formData.CustomerName || 'N/A'}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <TextField
                                                fullWidth
                                                label="Customer Name *"
                                                value={formData.CustomerName}
                                                onChange={(e) => handleInputChange('CustomerName', e.target.value)}
                                                error={!!errors.CustomerName}
                                                helperText={errors.CustomerName}
                                                size="small"
                                                sx={{
                                                    '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
                                                    '& .MuiFormHelperText-root': { fontSize: '0.65rem' }
                                                }}
                                            />
                                        )
                                    )}
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Customer Type *
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.CustomerTypeId === 1 ? 'Events' : 'Others'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ fontSize: '0.75rem' }}>Customer Type *</InputLabel>
                                            <Select
                                                value={formData.CustomerTypeId}
                                                label="Customer Type *"
                                                onChange={(e) => handleInputChange('CustomerTypeId', e.target.value)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    '& .MuiSelect-select': { py: 0.5 }
                                                }}
                                            >
                                                <MenuItem value={1} sx={{ fontSize: '0.75rem' }}>Events</MenuItem>
                                                <MenuItem value={2} sx={{ fontSize: '0.75rem' }}>Others</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={4}>
                                            {mode === 'view' ? (
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                        Contact Number 1
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                        {formData.ContactNumber1 || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    label="Contact Number 1"
                                                    value={formData.ContactNumber1}
                                                    onChange={(e) => handleInputChange('ContactNumber1', e.target.value)}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                        '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PhoneIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            {mode === 'view' ? (
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                        Contact Number 2
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                        {formData.ContactNumber2 || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    label="Contact Number 2"
                                                    value={formData.ContactNumber2}
                                                    onChange={(e) => handleInputChange('ContactNumber2', e.target.value)}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                        '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PhoneIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                        <Grid item xs={4}>
                                            {mode === 'view' ? (
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                        GST Number
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                        {formData.GSTNumber || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    label="GST Number"
                                                    value={formData.GSTNumber}
                                                    onChange={(e) => handleInputChange('GSTNumber', e.target.value)}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                        '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                                    }}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {/* <Grid item xs={12}> */}
                                <Grid item xs={12} md={4}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Address Line 1
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.AddressLine1 || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Address Line 1"
                                            value={formData.AddressLine1}
                                            onChange={(e) => handleInputChange('AddressLine1', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LocationIcon sx={{ color: '#a0aec0', fontSize: 16 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Address Line 2
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.AddressLine2 || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Address Line 2"
                                            value={formData.AddressLine2}
                                            onChange={(e) => handleInputChange('AddressLine2', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Location
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.Location || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Location"
                                            value={formData.Location}
                                            onChange={(e) => handleInputChange('Location', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Pin Code
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.PinCode || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Pin Code"
                                            value={formData.PinCode}
                                            onChange={(e) => handleInputChange('PinCode', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                    {/* Event Information Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Box sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '6px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                }}>
                                    <LocationIcon sx={{ color: 'white', fontSize: 12 }} />
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '0.75rem', letterSpacing: '-0.3px' }}>
                                    Event Details
                                </Typography>
                            </Box>

                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={2}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Enquiry No
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.EnquiryNo || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Enquiry No"
                                            value={formData.EnquiryNo}
                                            onChange={(e) => handleInputChange('EnquiryNo', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Bill Date *
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.BillDate || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Bill Date *"
                                            type="date"
                                            value={formData.BillDate}
                                            onChange={(e) => handleInputChange('BillDate', e.target.value)}
                                            error={!!errors.BillDate}
                                            helperText={errors.BillDate}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 },
                                                '& .MuiFormHelperText-root': { fontSize: '0.65rem' }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Status *
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.Status || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ fontSize: '0.75rem' }}>Status *</InputLabel>
                                            <Select
                                                value={formData.Status}
                                                label="Status *"
                                                onChange={(e) => handleInputChange('Status', e.target.value)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    '& .MuiSelect-select': { py: 0.5 }
                                                }}
                                            >
                                                <MenuItem value="Pending" sx={{ fontSize: '0.75rem' }}>Pending</MenuItem>
                                                <MenuItem value="In Progress" sx={{ fontSize: '0.75rem' }}>In Progress</MenuItem>
                                                <MenuItem value="Completed" sx={{ fontSize: '0.75rem' }}>Completed</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Event Name
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.EventName || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Event Name"
                                            value={formData.EventName}
                                            onChange={(e) => handleInputChange('EventName', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Event Location
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.EventLocation || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Event Location"
                                            value={formData.EventLocation}
                                            onChange={(e) => handleInputChange('EventLocation', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Event Address
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.EventAddress || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Event Address"
                                            value={formData.EventAddress}
                                            onChange={(e) => handleInputChange('EventAddress', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={1.4}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Event Days
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.EventDays || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Rent Days"
                                            type="number"
                                            value={formData.EventDays}
                                            onChange={(e) => handleInputChange('EventDays', e.target.value)}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={1.7}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Expected Time
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formatTimeToAMPM(formData.ExpectedTime)}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Expected Time"
                                            type="time"
                                            value={formData.ExpectedTime || ''}
                                            onChange={(e) => handleInputChange('ExpectedTime', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                step: 300, // 5 min intervals
                                            }}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={1.7}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Program Date
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.ProgramDate || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Program Date"
                                            type="date"
                                            value={formData.ProgramDate}
                                            onChange={(e) => handleInputChange('ProgramDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={1.7}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Delivery Date
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.DeliveryDate || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Delivery Date"
                                            type="date"
                                            value={formData.DeliveryDate}
                                            onChange={(e) => handleInputChange('DeliveryDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={1.5}>
                                    {mode === 'view' ? (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                                Return Date
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#000', fontWeight: 500 }}>
                                                {formData.DeliveryReturnDate || 'N/A'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Return Date"
                                            type="date"
                                            value={formData.DeliveryReturnDate}
                                            onChange={(e) => handleInputChange('DeliveryReturnDate', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            sx={{
                                                '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', borderRadius: '6px' }, '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 }, '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' } },
                                                '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 }
                                            }}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                    {/* Items Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{
                            p: 1,
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            bgcolor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '6px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                                    }}>
                                        <InventoryIcon sx={{ color: 'white', fontSize: 12 }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '0.75rem', letterSpacing: '-0.3px' }}>
                                        Items
                                    </Typography>
                                </Box>
                                {mode !== 'view' && (
                                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<ListIcon />}
                                            onClick={handleOpenGetItemsModal}
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                borderRadius: '6px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                py: 0.4,
                                                px: 1.25,
                                                height: '26px',
                                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                                }
                                            }}
                                        >
                                            Get Items
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {errors.items && (
                                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem', display: 'block', mb: 1, ml: 1 }}>
                                    {errors.items}
                                </Typography>
                            )}

                            {/* Forecast Loading Indicator */}
                            {forecastLoading && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    mb: 1, 
                                    p: 1, 
                                    bgcolor: '#eff6ff', 
                                    borderRadius: 1,
                                    border: '1px solid #bfdbfe'
                                }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#1e40af' }}>
                                        Checking item availability...
                                    </Typography>
                                </Box>
                            )}

                            {/* Forecast Conflicts Summary */}
                            {Object.keys(itemConflicts).length > 0 && (
                                <Alert 
                                    severity="warning" 
                                    sx={{ 
                                        fontSize: '0.75rem', 
                                        py: 0.5, 
                                        mb: 1,
                                        '& .MuiAlert-icon': {
                                            fontSize: 18
                                        }
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        ⚠️ Item Availability Conflicts Detected
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                        {Object.keys(itemConflicts).length} item(s) are already booked during the selected delivery period. 
                                        Please review the highlighted items below.
                                    </Typography>
                                </Alert>
                            )}

                            <TableContainer component={Paper} sx={{
                                boxShadow: 'none',
                                border: 'none',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                <Table size="small" sx={{
                                    '& .MuiTableCell-root': {
                                        padding: '6px 10px'
                                    }
                                }}>
                                    <TableHead>
                                        <TableRow sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        }}>
                                            <TableCell sx={{
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                color: 'white',
                                                border: 'none',
                                                width: '250px',
                                                py: 1,
                                                px: 1.5
                                            }}>
                                                Item
                                            </TableCell>
                                            <TableCell sx={{
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                color: 'white',
                                                border: 'none',
                                                width: '80px',
                                                textAlign: 'center',
                                                py: 1,
                                                px: 0.75
                                            }}>
                                                Qty
                                            </TableCell>
                                            <TableCell sx={{
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                color: 'white',
                                                border: 'none',
                                                width: '100px',
                                                textAlign: 'center',
                                                py: 1,
                                                px: 0.75
                                            }}>
                                                Rate (₹)
                                            </TableCell>
                                            {mode !== 'view' && (
                                                <TableCell sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.75rem',
                                                    color: 'white',
                                                    border: 'none',
                                                    width: '60px',
                                                    textAlign: 'center',
                                                    py: 1,
                                                    px: 0.75
                                                }}>
                                                    Action
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {/* Check if there are any items with valid ItemId */}
                                        {formData.items.filter(item => item.ItemId).length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={mode === 'view' ? 3 : 4}
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 4,
                                                        border: '1px solid #d1d5db'
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        color: '#6b7280'
                                                    }}>
                                                        <InventoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                                                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                            No items added yet
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                                            Click "Get Items" to add items to this enquiry
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            formData.items.filter(item => item.ItemId).map((item, index) => {
                                                // Find the original index for proper handling
                                                const originalIndex = formData.items.findIndex(originalItem =>
                                                    originalItem.ItemId === item.ItemId &&
                                                    originalItem.ItemName === item.ItemName
                                                );
                                                
                                                // Check if this item has a conflict
                                                const conflict = itemConflicts[item.ItemId];
                                                const hasConflict = conflict && conflict.hasConflict; // Quantity conflict
                                                const hasDateOverlap = conflict && conflict.hasDateOverlap; // Date overlap only
                                                const showWarning = hasConflict || hasDateOverlap;
                                                
                                                // Color scheme: Red for quantity conflict, Yellow for date overlap only
                                                const bgColor = hasConflict ? '#fef2f2' : (hasDateOverlap ? '#fffbeb' : 'white');
                                                const bgColorEven = hasConflict ? '#fee2e2' : (hasDateOverlap ? '#fef3c7' : '#f8fafc');
                                                const bgColorHover = hasConflict ? '#fecaca' : (hasDateOverlap ? '#fde68a' : '#f1f5f9');
                                                const borderColor = hasConflict ? '#ef4444' : (hasDateOverlap ? '#f59e0b' : 'none');
                                                const textColor = hasConflict ? '#dc2626' : (hasDateOverlap ? '#d97706' : '#1f2937');
                                                const iconColor = hasConflict ? '#ef4444' : '#f59e0b';
                                                
                                                return (
                                                    <TableRow key={originalIndex} sx={{
                                                        '&:nth-of-type(even)': { 
                                                            backgroundColor: bgColorEven
                                                        },
                                                        '&:hover': { 
                                                            backgroundColor: bgColorHover
                                                        },
                                                        backgroundColor: bgColor,
                                                        transition: 'background-color 0.2s',
                                                        borderLeft: showWarning ? `4px solid ${borderColor}` : 'none'
                                                    }}>
                                                        <TableCell sx={{
                                                            border: 'none',
                                                            borderBottom: '1px solid #e5e7eb',
                                                            py: 0.75,
                                                            px: 1.5
                                                        }}>
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography sx={{ 
                                                                        fontSize: '0.75rem', 
                                                                        color: showWarning ? textColor : '#1f2937', 
                                                                        fontWeight: showWarning ? 600 : 500 
                                                                    }}>
                                                                        {item.ItemName || 'N/A'}
                                                                    </Typography>
                                                                    {showWarning && (
                                                                        <WarningIcon sx={{ 
                                                                            fontSize: 16, 
                                                                            color: iconColor,
                                                                            animation: hasConflict ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                                                                        }} />
                                                                    )}
                                                                </Box>
                                                                {showWarning && (
                                                                    <Typography sx={{ 
                                                                        fontSize: '0.65rem', 
                                                                        color: textColor,
                                                                        mt: 0.25,
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {hasConflict ? '⚠️' : 'ℹ️'} {conflict.message}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            border: 'none',
                                                            borderBottom: '1px solid #e5e7eb',
                                                            textAlign: 'center',
                                                            py: 0.5,
                                                            px: 0.75
                                                        }}>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                value={item.Quantity}
                                                                onChange={(e) => handleInputChange('Quantity', e.target.value, originalIndex)}
                                                                error={!!errors[`Quantity_${originalIndex}`]}
                                                                size="small"
                                                                InputProps={{ readOnly: mode === 'view' }}
                                                                sx={{
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '0.7rem',
                                                                        textAlign: 'center',
                                                                        py: 0.4,
                                                                        px: 0.5,
                                                                        backgroundColor: showWarning ? (hasConflict ? '#fee2e2' : '#fef3c7') : 'white'
                                                                    },
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: showWarning ? borderColor : '#d1d5db'
                                                                    },
                                                                    '& .MuiInputBase-root': {
                                                                        height: '26px',
                                                                        backgroundColor: showWarning ? (hasConflict ? '#fee2e2' : '#fef3c7') : 'white'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            border: 'none',
                                                            borderBottom: '1px solid #e5e7eb',
                                                            textAlign: 'center',
                                                            py: 0.5,
                                                            px: 0.75
                                                        }}>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                value={item.Rate}
                                                                onChange={(e) => handleInputChange('Rate', e.target.value, originalIndex)}
                                                                error={!!errors[`Rate_${originalIndex}`]}
                                                                size="small"
                                                                InputProps={{ readOnly: mode === 'view' }}
                                                                sx={{
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '0.7rem',
                                                                        textAlign: 'right',
                                                                        py: 0.4,
                                                                        px: 0.5,
                                                                        backgroundColor: showWarning ? (hasConflict ? '#fee2e2' : '#fef3c7') : 'white'
                                                                    },
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: showWarning ? borderColor : '#d1d5db'
                                                                    },
                                                                    '& .MuiInputBase-root': {
                                                                        height: '26px',
                                                                        backgroundColor: showWarning ? (hasConflict ? '#fee2e2' : '#fef3c7') : 'white'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        {mode !== 'view' && (
                                                            <TableCell sx={{
                                                                border: 'none',
                                                                borderBottom: '1px solid #e5e7eb',
                                                                textAlign: 'center',
                                                                py: 0.5,
                                                                px: 0.75
                                                            }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => removeItem(originalIndex)}
                                                                    sx={{
                                                                        color: '#ef4444',
                                                                        p: 0.4,
                                                                        '&:hover': {
                                                                            backgroundColor: '#fee2e2',
                                                                            color: '#dc2626'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                    {/* Remarks Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#f56565', mr: 1, width: 24, height: 24 }}>
                                    <NotesIcon sx={{ fontSize: 14 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.9rem' }}>
                                    Additional Notes
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="Remarks"
                                multiline
                                rows={3}
                                value={formData.Remarks}
                                onChange={(e) => handleInputChange('Remarks', e.target.value)}
                                placeholder="Any additional remarks or special instructions..."
                                size="small"
                                InputProps={{ readOnly: mode === 'view' }}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiInputLabel-root': { fontSize: '0.75rem', fontWeight: 600 },
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.75rem',
                                        borderRadius: '6px',
                                        '&:hover fieldset': { borderColor: '#667eea' },
                                        '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' }
                                    },
                                    '& .MuiInputBase-input': { fontSize: '0.75rem', py: 1 }
                                }}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ p: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 0.75 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 2,
                        borderColor: '#cbd5e1',
                        color: '#64748b',
                        '&:hover': {
                            borderColor: '#94a3b8',
                            bgcolor: '#f1f5f9'
                        }
                    }}
                >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                </Button>

                {mode !== 'view' && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 2,
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                            },
                            '&:disabled': {
                                background: '#cbd5e1',
                                color: '#94a3b8'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={16} sx={{ color: 'white', mr: 0.5 }} /> : null}
                        {loading ? 'Saving...' : mode === 'edit' ? 'Update Enquiry' : 'Create Enquiry'}
                    </Button>
                )}
            </DialogActions>

            {/* Success/Error Message Snackbar */}
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccessMessage(false)}
                    severity={successMessage.includes('successfully') ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Get Items Modal */}
            <Dialog
                open={getItemsModalOpen}
                onClose={handleCloseGetItemsModal}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '6px',
                        m: 0.5,
                        maxHeight: '75vh',
                        width: '85%',
                        maxWidth: '1000px'
                    }
                }}
            >
                <DialogTitle sx={{
                    py: 0.5,
                    px: 1.5,
                    borderBottom: '1px solid #d1d5db',
                    bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    color: '#fff'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ListIcon sx={{ color: '#fff', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                                Get Items ({getFilteredItems().length} available)
                            </Typography>
                        </Box>
                        <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
                    {getItemsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.75rem' }}>Loading items...</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Search */}
                            <Box sx={{ mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search items by name, HSN code, or item code..."
                                    value={getItemsSearchValue}
                                    onChange={(e) => handleGetItemsSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: getItemsSearchValue && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleGetItemsSearch('')}
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 12 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiInputBase-input': { py: 0.75, fontSize: '0.75rem' },
                                        '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                                    }}
                                />
                            </Box>

                            {/* Selected Items Summary */}
                            {selectedItems.length > 0 && (
                                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>
                                        Selected Items ({selectedItems.length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selectedItems.slice(0, 8).map(item => (
                                            <Chip
                                                key={item.Id}
                                                label={`${item.Name} (${item.selectedQuantity})`}
                                                size="small"
                                                onDelete={() => handleItemSelection(item, false)}
                                                sx={{ fontSize: '0.65rem', height: '20px' }}
                                            />
                                        ))}
                                        {selectedItems.length > 8 && (
                                            <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Items Table */}
                            <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '28px' }}>
                                                <th style={{ padding: '3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '5%' }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                                                        indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                getPaginatedItems().forEach(item => {
                                                                    if (!selectedItems.find(si => si.Id === item.Id)) {
                                                                        handleItemSelection(item, true);
                                                                    }
                                                                });
                                                            } else {
                                                                getPaginatedItems().forEach(item => {
                                                                    handleItemSelection(item, false);
                                                                });
                                                            }
                                                        }}
                                                        sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                                                    />
                                                </th>
                                                <th style={{ padding: '3px 6px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '25%', fontSize: '0.65rem' }}>Item</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>HSN</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>Qty</th>
                                                <th style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getPaginatedItems().map((item, index) => {
                                                const selectedItem = selectedItems.find(si => si.Id === item.Id);
                                                const isSelected = !!selectedItem;
                                                const isAlreadyAdded = isItemAlreadyInSale(item.Id);

                                                return (
                                                    <tr key={item.Id} style={{
                                                        borderBottom: '1px solid #e5e7eb',
                                                        background: isSelected ? '#eff6ff' : (index % 2 === 0 ? '#fff' : '#f9fafb'),
                                                        opacity: isAlreadyAdded ? 0.5 : 1,
                                                        height: '30px'
                                                    }}>
                                                        <td style={{ padding: '3px', textAlign: 'center' }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={isSelected}
                                                                disabled={isAlreadyAdded}
                                                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '3px 6px', fontWeight: 500, fontSize: '0.65rem' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>{item.Name}</span>
                                                                {isAlreadyAdded && (
                                                                    <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.55rem', height: 12 }} />
                                                                )}
                                                            </Box>
                                                        </td>
                                                        <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>

                                                        <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                                            {isSelected ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={selectedItem.selectedQuantity}
                                                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                                                    inputProps={{ min: 1 }}
                                                                    sx={{
                                                                        width: 45,
                                                                        '& .MuiInputBase-input': {
                                                                            py: 0.15,
                                                                            px: 0.15,
                                                                            fontSize: '0.65rem',
                                                                            textAlign: 'center',
                                                                            color: '#000'
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>1</span>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                                            {isSelected ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={selectedItem.selectedRate}
                                                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                                                    inputProps={{ min: 0 }}
                                                                    sx={{
                                                                        width: 65,
                                                                        '& .MuiInputBase-input': {
                                                                            py: 0.15,
                                                                            px: 0.15,
                                                                            fontSize: '0.65rem',
                                                                            textAlign: 'right',
                                                                            color: '#000'
                                                                        }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                                                                    ₹{(item.EventSaleRate || item.Rate || 0).toFixed(2)}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {getPaginatedItems().length === 0 && (
                                                <tr>
                                                    <td colSpan="5" style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                                                        {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>

                            {/* Pagination */}
                            {getTotalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentItemsPage === 1}
                                            sx={{ fontSize: '0.75rem', py: 0.25, px: 0.75 }}
                                        >
                                            Previous
                                        </Button>
                                        <Typography variant="caption" sx={{ mx: 1.5 }}>
                                            Page {currentItemsPage} of {getTotalPages}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => setCurrentItemsPage(prev => Math.min(getTotalPages, prev + 1))}
                                            disabled={currentItemsPage === getTotalPages}
                                            sx={{ fontSize: '0.75rem', py: 0.25, px: 0.75 }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Button
                            onClick={handleCloseGetItemsModal}
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: '0.75rem', py: 0.25, px: 1.5 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddSelectedItems}
                            variant="contained"
                            size="small"
                            disabled={selectedItems.length === 0 || addingItemsLoading}
                            sx={{
                                bgcolor: '#1e40af',
                                '&:hover': { bgcolor: '#1d4ed8' },
                                fontSize: '0.75rem',
                                py: 0.25,
                                px: 1.5
                            }}
                        >
                            {addingItemsLoading ? (
                                <>
                                    <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                    Adding...
                                </>
                            ) : (
                                `Add Selected (${selectedItems.length})`
                            )}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        textAlign: 'center',
                        p: 1
                    }
                }}
            >
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{
                            bgcolor: '#10b981',
                            width: 64,
                            height: 64,
                            mb: 1
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                        </Avatar>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            color: '#065f46',
                            mb: 1
                        }}>
                            Success!
                        </Typography>
                        <Typography variant="body1" sx={{
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            {successMessage}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            mt: 1
                        }}>
                            This dialog will close automatically in 3 seconds
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
};

export default EventItemEnquiryModalSingle;





